import { Autocomplete, TextField, ThemeProvider, createFilterOptions, createTheme } from '@mui/material';
import React, { memo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styles from './DashboardInput.module.css';

const DFreeSoloInput = ({
    label,
    placeHolder,
    onChange,
    error,
    options,
    disabled,
    size,
    customClass,
    value,
    errorMsg,
    restrictSpecialChars = false,
    inputRegex
}) => {
    const theme = createTheme({
        typography: {
            htmlFontSize: 9.8,
        },
    });
    const filterOptions = createFilterOptions({
        ignoreCase: true,
        limit: 50,
    });

    const { control } = useForm();

    return (
        <ThemeProvider theme={theme}>
            <div className={`${styles.select_container} ${customClass} `} style={{ width: `${size}` }}>
                <label>{label}</label>
                <Controller
                    name="autocomplete"
                    control={control}
                    render={({ field }) => (
                        <Autocomplete
                            {...field}
                            value={value}
                            freeSolo
                            disablePortal
                            options={options}
                            sx={{
                                height: '4.2rem',
                                fontSize: '1.4rem',
                                transition: "0",
                                ".Mui-expanded": {
                                    transition: "none"
                                },
                                ".MuiOutlinedInput-notchedOutline": {
                                    border: "0",
                                },
                                ".MuiAutocomplete-input": {
                                    fontSize: "1.4rem",
                                    fontFamily: "GilroyMedium",
                                    height: "2.6rem",
                                },
                            }}
                            componentsProps={{
                                popper: {
                                    sx: {
                                        fontFamily: 'gilroyMedium'
                                    },
                                    modifiers: [
                                        {
                                            name: 'flip',
                                            enabled: false
                                        },
                                        {
                                            name: 'preventOverflow',
                                            enabled: true
                                        },
                                    ]
                                }
                            }}
                            size='small'
                            className={`${styles.select_wrapper}  ${error ? styles.error : ''}`}
                            disabled={disabled}
                            filterOptions={filterOptions}
                            onChange={(e, newValue) => {
                                field.onChange(newValue);
                                if (onChange) {
                                    onChange(newValue);
                                }
                            }}
                            onInputChange={(e, newValue) => {
                                // Filter in real-time using provided inputRegex; fallback to default when restrictSpecialChars is enabled
                                const hasCustomRegex = !!inputRegex;
                                if ((hasCustomRegex || restrictSpecialChars) && newValue) {
                                    const rejectRegex = hasCustomRegex ? inputRegex : /[^a-zA-Z0-9\s]+/g;
                                    const filteredValue = newValue.replace(rejectRegex, "");
                                    field.onChange(filteredValue);
                                    if (onChange) onChange(filteredValue);
                                } else {
                                    field.onChange(newValue);
                                    if (onChange) onChange(newValue);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    // onChange={(e) => }
                                    error={error}
                                    placeholder={!placeHolder ? label : placeHolder}
                                    onKeyDown={(e) => {
                                        // Prevent disallowed characters from being typed in real-time
                                        if (inputRegex || restrictSpecialChars) {
                                            const rejectRegex = inputRegex || /[^a-zA-Z0-9\s]/;
                                            // Allow: Backspace, Delete, Tab, Escape, Enter, Arrow keys, Home, End
                                            const allowedKeys = [
                                                'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                                                'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                                                'Home', 'End'
                                            ];
                                            // Allow Ctrl/Cmd + A, C, V, X, Z
                                            if (e.ctrlKey || e.metaKey) {
                                                if (['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) {
                                                    return; // Allow these shortcuts
                                                }
                                            }
                                            if (!allowedKeys.includes(e.key)) {
                                                // Use a non-global clone to avoid lastIndex issues
                                                const singleCharReject = new RegExp(rejectRegex.source);
                                                if (singleCharReject.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }
                                        }
                                    }}
                                    onPaste={(e) => {
                                        // Filter special characters from pasted content
                                        if (inputRegex || restrictSpecialChars) {
                                            e.preventDefault();
                                            const pastedText = e.clipboardData.getData('text');
                                            const rejectRegex = inputRegex || /[^a-zA-Z0-9\s]+/g;
                                            const filteredText = pastedText.replace(rejectRegex, '');
                                            // Insert filtered text at cursor position
                                            const input = e.target;
                                            const start = input.selectionStart || 0;
                                            const end = input.selectionEnd || 0;
                                            const currentValue = params.inputProps?.value || '';
                                            const newValue = currentValue.substring(0, start) + filteredText + currentValue.substring(end);
                                            // Update the value through onChange and field state
                                            field.onChange(newValue);
                                            if (onChange) onChange(newValue);
                                        }
                                    }}
                                />
                            )}
                        />
                    )}
                />
                {error && <p className={styles.error_message}>{errorMsg}</p>}

            </div>
        </ThemeProvider>
    );
};

export default memo(DFreeSoloInput);
